<template>
  <div class="app-tabs w-full flex flex-col">
    <TabGroup as="div" vertical :selected-index="model" class="flex w-full flex-col sm:flex-row" v-bind="$attrs" @change="onChange">
      <!-- Mobile select list -->
      <div class="sm:hidden w-full mb-4">
        <label for="vertical-tabs" class="sr-only">Select a tab</label>
        <select 
          id="vertical-tabs" 
          class="block w-full rounded-md border-slate-300 py-2 pl-3 pr-10 text-base focus:border-primary focus:outline-none focus:ring-primary sm:text-sm"
          :value="model"
          @change="handleMobileTabChange"
        >
          <option v-for="(item, index) in items" :key="item.label" :value="index" :selected="index === model">
            {{ item.label }}
          </option>
        </select>
      </div>

      <!-- Desktop tab list -->
      <TabList class='hidden sm:flex flex-col border-r border-slate-300 w-52' aria-label="Tabs">
        <Tab
          v-for="(item, index) in items"
          :key="item.label"
          v-slot="{ selected, disabled }"
          :disabled="item.disabled || false"
          as="template"
        >
          <RouterLink
            v-if="item.to"
            :to="item.to"
            :class="tabButtonClass(selected)"
          >
            <span class="truncate">{{ item.label }}</span>
          </RouterLink>

          <button
            v-else
            :class="tabButtonClass(selected)"
          >
            <slot name="icon" :item="item" :index="index" :selected="selected" :disabled="disabled" />
            <slot :item="item" :index="index" :selected="selected" :disabled="disabled">
              <span class="truncate">{{ item.label }}</span>
            </slot>
            <slot :name="`${item.slot}-badge` || 'badge'" :item="item" :index="index" :selected="selected" :disabled="disabled">
              <span
                v-if="item.badge"
                :class="[
                  selected ? 'bg-secondary text-primary' : 'bg-slate-100 text-black',
                  'ml-2 rounded-full py-0.5 text-xs font-medium'
                ]"
              >
                {{ item.badge }}
              </span>
            </slot>
          </button>
        </Tab>
      </TabList>

      <div class="tab-content py-4">
        <TabPanels v-if="content">
          <TabPanel v-for="(item, index) of items" :key="index" v-slot="{ selected }">
            <slot :name="item.slot || 'content'" :item="item" :index="index" :selected="selected">
              {{ item.content }}
            </slot>
          </TabPanel>
        </TabPanels>
      </div>
    </TabGroup>
  </div>
</template>

<script lang="ts" setup>
import { PropType } from 'vue'
import { RouteLocationRaw, useRouter } from 'vue-router'
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue'

export type TabItem = {
  label: string
  content?: string
  badge?: string | number | undefined
  disabled?: boolean
  slot?: string
  to?: RouteLocationRaw
}

const model = defineModel({
  type: Number,
  default: 0,
})

const router = useRouter()

const props = defineProps({
  items: {
    type: Array as PropType<TabItem[]>,
    required: true,
  },
  content: {
    type: Boolean,
    default: true,
  }
})

const onChange = (index: number) => {
  model.value = index
}

const handleMobileTabChange = (event: Event) => {
  const target = event.target as HTMLSelectElement
  const newIndex = parseInt(target.value)
  model.value = newIndex
  
  const selectedItem = props.items[newIndex]
  if (selectedItem && selectedItem.to) {
    router.push(selectedItem.to)
  }
}

const tabButtonClass = (selected: boolean) => [
  selected ? 'bg-slate-50 text-primary font-semibold' : 'text-slate-600 hover:bg-slate-100 hover:text-black',
  'border-r-2',
  selected ? 'border-primary' : 'border-transparent',
  'flex items-center py-2 text-sm focus:outline-none transition-colors duration-200'
]
</script>
